import { ChevronDown, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { useState } from "react";
import { useTranslation } from "@/packages/utils";
import { useSidebar } from "./SidebarContext";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import type { WorkspaceProps } from "./types";

export function SidebarWorkspace({
  name,
  initials,
  color,
  currentSlug,
}: WorkspaceProps) {
  const { t } = useTranslation(["sidebar"]);
  const { collapsed, toggleCollapsed } = useSidebar();
  const [popoverOpen, setPopoverOpen] = useState(false);

  if (collapsed) {
    return (
      <div className="flex flex-col items-center gap-1.5 py-3">
        <div className="relative group cursor-pointer">
          <div
            className="absolute inset-0 rounded-lg blur-[6px] opacity-40 group-hover:opacity-60 transition-opacity duration-300"
            style={{ backgroundColor: color }}
          />
          <div
            className="relative flex h-8 w-8 items-center justify-center rounded-lg text-[11px] font-bold tracking-wide text-white shadow-sm"
            style={{ backgroundColor: color }}
          >
            {initials}
          </div>
        </div>

        <button
          type="button"
          onClick={toggleCollapsed}
          title={`${t("sidebar:actions.openSidebar")} (⌘B)`}
          className="flex h-6 w-6 items-center justify-center rounded-md text-muted hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
        >
          <PanelLeftOpen size={13} strokeWidth={2} />
        </button>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-2.5 px-4 py-3.5">
      <div className="relative">
        <div
          className="absolute inset-0 rounded-lg blur-[6px] opacity-30 group-hover:opacity-50 transition-opacity duration-300"
          style={{ backgroundColor: color }}
        />
        <div
          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold tracking-wide text-white shadow-sm"
          style={{ backgroundColor: color }}
        >
          {initials}
        </div>
      </div>

      <Popover.Root open={popoverOpen} onOpenChange={setPopoverOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-1 group/name"
          >
            <span className="truncate text-[13px] font-semibold text-primary transition-colors">
              {name}
            </span>
            <ChevronDown
              size={13}
              className="shrink-0 text-muted group-hover/name:text-secondary transition-colors"
            />
          </button>
        </Popover.Trigger>
        <WorkspaceSwitcher
          currentSlug={currentSlug}
          onClose={() => setPopoverOpen(false)}
        />
      </Popover.Root>

      <div className="flex items-center gap-0.5">
        {/* <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            type="button"
            onClick={onSearch}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
          >
            <Search size={14} strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={onNew}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
          >
            <Pencil size={14} strokeWidth={2} />
          </button>
        </div> */}

        <button
          type="button"
          onClick={toggleCollapsed}
          title={`${t("sidebar:actions.closeSidebar")} (⌘B)`}
          className="flex h-7 w-7 items-center justify-center rounded-md text-muted hover:bg-card-hover/50 hover:text-secondary transition-all duration-150"
        >
          <PanelLeftClose size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}
